import React, { useState } from 'react';
import Icon from 'components/AppIcon';

export default function DemoModeSection({ showToast, isAdmin, onNavigateToRoles }) {
  const [demoMode, setDemoMode] = useState(true);
  const [guidedDemo, setGuidedDemo] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);

  const handleReset = () => {
    setShowResetModal(false);
    showToast('Demo data reset — refreshing…');
    setTimeout(() => window.location?.reload(), 1500);
  };

  return (
    <div>
      {/* Demo Mode Toggle Card */}
      <div className={`rounded-2xl border-2 shadow-sm p-6 mb-6 ${
        demoMode ? 'border-green-200 bg-green-50/30' : 'border-slate-200 bg-white'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              demoMode ? 'bg-green-100' : 'bg-slate-100'
            }`}>
              <Icon name="FlaskConical" size={20} color={demoMode ? '#16a34a' : '#64748b'} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Demo Mode</h3>
              <p className="text-sm text-slate-500">Control the platform's demonstration environment</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {demoMode && (
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">Active</span>
            )}
            <button
              onClick={() => {
                if (demoMode) {
                  if (isAdmin) setShowDisableModal(true);
                } else {
                  setDemoMode(true);
                  showToast('Demo mode enabled');
                }
              }}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                demoMode ? 'bg-green-500' : 'bg-slate-200'
              }`}
            >
              <span className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform" style={{ transform: demoMode ? 'translateX(22px)' : 'translateX(2px)' }} />
            </button>
          </div>
        </div>

        {demoMode ? (
          <div>
            <div className="flex items-start gap-3 p-4 bg-green-100/50 rounded-xl border border-green-200 mb-5">
              <Icon name="Info" size={16} color="#16a34a" className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-800">Demo mode is active. All data shown is sample data for demonstration purposes only. No real transactions or communications are processed.</p>
            </div>

            {/* Guided Demo Toggle */}
            <div className="flex items-center justify-between py-4 border-t border-green-200/50">
              <div>
                <p className="text-sm font-medium text-slate-700">Guided Demo Overlay</p>
                <p className="text-xs text-slate-500">Enable in-app guided walkthrough for new users</p>
              </div>
              <button
                onClick={() => setGuidedDemo(!guidedDemo)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  guidedDemo ? 'bg-[#3b4c4c]' : 'bg-slate-200'
                }`}
              >
                <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform" style={{ transform: guidedDemo ? 'translateX(22px)' : 'translateX(2px)' }} />
              </button>
            </div>

            {/* Active Role */}
            <div className="flex items-center justify-between py-4 border-t border-green-200/50">
              <div>
                <p className="text-sm font-medium text-slate-700">Active Demo Role</p>
                <p className="text-xs text-slate-500">Currently viewing as Admin</p>
              </div>
              <button
                onClick={onNavigateToRoles}
                className="text-xs font-semibold text-[#3b4c4c] hover:underline flex items-center gap-1"
              >
                Switch Role
                <Icon name="ChevronRight" size={12} />
              </button>
            </div>

            {/* Reset Button */}
            <div className="pt-4 border-t border-green-200/50">
              <button
                onClick={() => setShowResetModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-red-200 text-red-600 bg-white hover:bg-red-50 transition-colors"
              >
                <Icon name="RotateCcw" size={15} />
                Reset Demo Data
              </button>
              <p className="text-xs text-slate-400 mt-2">Restores all seeded sample data to default state.</p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
            <Icon name="AlertTriangle" size={16} color="#d97706" className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-amber-800 font-medium mb-1">Demo mode is disabled</p>
              <p className="text-sm text-amber-700">The platform is connected to live data sources. All actions are real.</p>
            </div>
          </div>
        )}
      </div>

      {/* Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <Icon name="RotateCcw" size={20} color="#dc2626" />
              </div>
              <h3 className="text-base font-semibold text-slate-900">Reset Demo Data</h3>
            </div>
            <p className="text-sm text-slate-600 mb-6">Reset all demo data to default? This will restore all seeded sample data and cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowResetModal(false)} className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleReset} className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors">Confirm Reset</button>
            </div>
          </div>
        </div>
      )}

      {/* Disable Demo Modal */}
      {showDisableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} color="#d97706" />
              </div>
              <h3 className="text-base font-semibold text-slate-900">Disable Demo Mode</h3>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 mb-5">
              <p className="text-sm text-amber-800">Disabling demo mode will connect the platform to live data sources. Ensure all integrations are configured before proceeding.</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowDisableModal(false)} className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button
                onClick={() => { setDemoMode(false); setShowDisableModal(false); showToast('Demo mode disabled'); }}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 transition-colors"
              >
                Disable Demo Mode
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
